<script setup lang="ts">
import type { Placement } from '@floating-ui/vue'
import {
  autoUpdate,
  flip,
  offset,
  shift,
  useFloating,
} from '@floating-ui/vue'

const {
  text,
  placement = 'top',
  delay,
} = defineProps<{
  text?: string
  placement?: Placement
  delay?: number
}>()

const triggerRef = ref<HTMLElement | null>(null)
const tooltipRef = ref<HTMLElement | null>(null)
const isVisible = ref(false)
const timeoutId = ref<number | null>(null)

function showTooltip() {
  if (delay) {
    timeoutId.value = window.setTimeout(() => {
      isVisible.value = true
    }, delay)
  }
  else {
    isVisible.value = true
  }
}

function hideTooltip() {
  if (timeoutId.value !== null) {
    clearTimeout(timeoutId.value)
    timeoutId.value = null
  }
  isVisible.value = false
}

const middleware = [
  offset(8),
  flip(),
  shift({ padding: 5 }),
]

const { floatingStyles } = useFloating(triggerRef, tooltipRef, {
  placement,
  middleware,
  whileElementsMounted: autoUpdate,
})
</script>

<template>
  <div class="relative inline-block">
    <div
      ref="triggerRef"
      class="flex items-center"
      @mouseenter="showTooltip"
      @mouseleave="hideTooltip"
    >
      <slot />
    </div>

    <Teleport to="body">
      <div
        v-show="isVisible"
        ref="tooltipRef"
        :style="floatingStyles"
        class="fixed z-[200]"
      >
        <Transition
          enter-active-class="transition duration-200 ease-out"
          enter-from-class="opacity-0 scale-95"
          enter-to-class="opacity-100 scale-100"
          leave-active-class="transition duration-150 ease-in"
          leave-from-class="opacity-100 scale-100"
          leave-to-class="opacity-0 scale-95"
        >
          <div
            v-show="isVisible"
            class="text-sm text-base-content bg-base-300 shadow-lg border border-base-content/20 rounded whitespace-nowrap"
            :class="[
              { 'px-2 py-1': text && !$slots['tooltip-content'] },
            ]"
          >
            <slot name="tooltip-content">
              {{ text }}
            </slot>
          </div>
        </Transition>
      </div>
    </Teleport>
  </div>
</template>
